/**
 * Shared currency formatting utilities.
 *
 * Usage:
 *   import { formatCurrency, getCurrencySymbol, DEFAULT_CURRENCY } from '../utils/currency';
 *
 *   formatCurrency(12500)            // "₹12,500.00"
 *   formatCurrency(12500, 'USD')     // "$12,500.00"
 *   formatCurrency(1500000, 'INR')   // "₹15,00,000.00"
 *   formatCurrencyShort(1500000)     // "₹15.0L"
 */

export const DEFAULT_CURRENCY = 'INR';

const SYMBOLS = {
  INR: '\u20B9',   // ₹
  USD: '$',
  EUR: '\u20AC',   // €
  GBP: '\u00A3',   // £
  SEK: 'kr',
  NOK: 'kr',
  AED: 'AED',
  SGD: 'S$',
  AUD: 'A$',
  CAD: 'C$',
  JPY: '\u00A5',   // ¥
  CHF: 'CHF',
};

export function getCurrencySymbol(currency) {
  return SYMBOLS[currency] || currency || SYMBOLS[DEFAULT_CURRENCY];
}

/**
 * Format a numeric value with currency symbol.
 * Uses en-IN locale for INR, en-US for others.
 */
export function formatCurrency(value, currency) {
  if (value == null || value === '') return '--';
  const cur = currency || DEFAULT_CURRENCY;
  const num = parseFloat(value);
  if (isNaN(num)) return '--';
  const sym = getCurrencySymbol(cur);
  const locale = cur === 'INR' ? 'en-IN' : 'en-US';
  const formatted = num.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${sym}${formatted}`;
}

/**
 * Short format for dashboards: ₹15.0L, $1.2M, $50K, etc.
 */
export function formatCurrencyShort(value, currency) {
  if (value == null || value === '') return '--';
  const cur = currency || DEFAULT_CURRENCY;
  const num = parseFloat(value);
  if (isNaN(num)) return '--';
  const sym = getCurrencySymbol(cur);

  if (cur === 'INR') {
    // Indian numbering: Cr, L, K
    if (Math.abs(num) >= 10000000) return `${sym}${(num / 10000000).toFixed(1)}Cr`;
    if (Math.abs(num) >= 100000) return `${sym}${(num / 100000).toFixed(1)}L`;
    if (Math.abs(num) >= 1000) return `${sym}${(num / 1000).toFixed(0)}K`;
    return `${sym}${num.toLocaleString('en-IN')}`;
  }

  // Western numbering: B, M, K
  if (Math.abs(num) >= 1000000000) return `${sym}${(num / 1000000000).toFixed(1)}B`;
  if (Math.abs(num) >= 1000000) return `${sym}${(num / 1000000).toFixed(1)}M`;
  if (Math.abs(num) >= 1000) return `${sym}${(num / 1000).toFixed(0)}K`;
  return `${sym}${num.toLocaleString('en-US')}`;
}
